import React, { useMemo, useState, useEffect, useCallback } from "react";
import Customtable from "../helper/Customtable";
import Swal from "sweetalert2";
import { post } from "../helper/api_helper";
import CommonModal from "../components/CommonModal";

const Bills = () => {
  // ------------------------------
  // Dynamic Form Field Config
  // ------------------------------
  const formFields = useMemo(() => [
    { name: "job_id_fk", label: "Job Ref", type: "select", required: true, col: 6,
      options: [
        { value: 0, label: "SVC-2024-441-South India Paper Mill-100000" },
        { value: 1, label: "SVC-2024-388-Chennai Steel Corp-100000" },
      ],
     },
    { name: "consignee_id_fk", label: "Consignee", type: "select", col: 6,
      options: []
     },
    { name: "bill_no", label: "Bill No", type: "text", col: 6 },
    { name: "bill_date", label: "Bill Date", type: "date", col: 6 },
    { name: "bill_type", label: "Bill Type", type: "select", col: 6,
      options: [
        { value: "", label: "-Select-" },
        { value: "Service Charge", label: "Service Charge" },
        { value: "Customs Duty", label: "Customs Duty" },
        { value: "Freight", label: "Freight" },
        { value: "Demurrage", label: "Demurrage" },
        { value: "Detention", label: "Detention" },
        { value: "other", label: "Other" },
      ]
     },
    { name: "debit_amount", label: "Debit Amount (₹)", type: "text", col: 6 },
    { name: "credit_aAmount", label: "Credit Amount (₹)", type: "text", col: 6 },
    { name: "payment_status", label: "Payment Status", type: "select", col: 6,
      options: [
        { value: "", label: "-Select-" },
        { value: "Unpaid", label: "Unpaid" },
        { value: "Partial", label: "Partial" },
        { value: "Paid", label: "Paid" },
      ]
     },
     { name: "remarks", label: "Remarks", type: "text", col: 12 },
  ], []);

  // ------------------------------
  // Initial Form State
  // ------------------------------
  const initialFormState = useMemo(() => {
    return formFields.reduce((acc, field) => {
      acc[field.name] = "";
      return acc;
    }, { Bills_id: 0 });
  }, [formFields]);

  const [formData, setFormData] = useState(initialFormState);
  const [BillsList, setBillsList] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [showModal, setShowModal] = useState(false);

  // ------------------------------
  // Alert Helper
  // ------------------------------
  const showAlert = useCallback((icon, title, text, timer = 2000) => {
    Swal.fire({
      icon,
      title,
      text: text || "Something went wrong.",
      timer,
      showConfirmButton: false,
    });
  }, []);

  // ------------------------------
  // Fetch Data
  // ------------------------------
  const fetchData = useCallback(async () => {
    try {
      const response = await post("/finance/Bills/list");
      if (response.status === 200) {
        setBillsList(response.data);
      } else {
        showAlert("error", response.status, response.message);
      }
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    }
  }, [showAlert]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ------------------------------
  // Edit Handler (FIXED POSITION)
  // ------------------------------
  const handleEdit = useCallback((row) => {
    setFormData({ ...initialFormState, ...row });
    setShowModal(true);
  }, [initialFormState]);

  // ------------------------------
  // Table Columns
  // ------------------------------
  const columns = useMemo(
    () => [
      { header: "S.No", label_condition: "s_no", enableColumnFilter: false, enableSorting: false },
      {
        header: "Actions",
        cell: ({ row }) => (
          <i
            className="ri-file-edit-fill text-primary cursor-pointer me-2"
            onClick={() => handleEdit(row)}
            title="Edit"
            style={{ fontSize: "18px" }}
          />
        ),
        enableSorting: false,
        enableColumnFilter: false,
      },
      { header: "Bill No.", accessorKey: "bill_no" },
      { header: "Job Ref", accessorKey: "job_no" },
      { header: "Consignee", accessorKey: "consignee" },
      { header: "Date", accessorKey: "date" },
      { header: "Ctrs", accessorKey: "ctrs" },
      { header: "Type", accessorKey: "type" },
      { header: "Debit (₹)", accessorKey: "debit" },
      { header: "Credit (₹)", accessorKey: "credit" },
      { header: "Balance (₹)", accessorKey: "balance" },
      { header: "Status", accessorKey: "status"},
    ],
    [handleEdit]
  );

  // ------------------------------
  // Input Change Handler
  // ------------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ------------------------------
  // Close Modal
  // ------------------------------
  const handleClose = () => {
    setShowModal(false);
    setFormData(initialFormState);
  };

  // ------------------------------
  // Submit Form
  // ------------------------------
  const handleSubmit = async () => {
    for (let field of formFields.filter((f) => f.required)) {
      if (!formData[field.name]) {
        showAlert("error", "Oops...", `${field.label} is required`);
        return;
      }
    }

    try {
      const response = await post("/finance/Bills", formData);
      if (response.status === 200) {
        showAlert("success", "Saved", response.message);
        fetchData();
        handleClose();
      } else {
        showAlert("error", response.status, response.message);
      }
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    }
  };

  // ------------------------------
  // Render
  // ------------------------------
  return (
    <>
      <div className="page-header d-flex justify-content-between align-items-center">
        <div>
          <div className="page-header-title">Bills & Invoices</div>
          <div className="page-header-sub">All service charges raised</div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>
          New Bill
        </button>
      </div>

      <div className="card basic-data-table mt-3">
        <div className="card-header">
          <h5 className="card-title mb-0">Bills List</h5>
        </div>
        <div className="card-body">
          <Customtable
            table_title="Bills List"
            columns={columns}
            data={BillsList}
            isGlobalFilter={true}
            isPagination={true}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            isExportButton={true}
          />
        </div>
      </div>

      <CommonModal
        show={showModal}
        title="Record Payment"
        formFields={formFields}
        formData={formData}
        onChange={handleChange}
        onClose={handleClose}
        onSubmit={handleSubmit}
        submitText="Save"
      />
    </>
  );
};

export default Bills;